/**
 * Iter 137 · Wave D · Shared route-driven breadcrumbs.
 *
 * Renders Domain → Module → Sub-tab based on the current pathname.
 * Reads from `NAV_ITEM_BY_PATH` in navigation.ts so it auto-tracks
 * every registered nav route. For unregistered routes (e.g. detail
 * pages) it falls back to the raw path segments cleaned up.
 */
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { NAV_DOMAINS, NAV_ITEM_BY_PATH } from "../config/navigation";

const HUMANIZE: Record<string, string> = {
  "pureh":              "Pure H OS",
  "dashboard":          "Executive Dashboard",
  "finance":            "Finance",
  "business-reality":   "Payments & COD",
  "invoices":           "Accounts Receivable",
  "expenses":           "Accounts Payable",
  "cash":               "Cash Flow",
  "pnl":                "P&L",
  "rd":                 "R&D",
  "operations":         "Operations",
  "inventory":          "Inventory",
  "product-master":     "Product Master",
  "company-setup":      "Company Setup",
  "workspace":          "Workspace",
  "commerce":           "Commerce",
  "marketing":          "Marketing",
  "launch":             "Launch",
  "growth":             "Growth",
  "governance":         "Governance",
  "knowledge":          "Knowledge",
  "administration":     "Administration",
  "admin":              "Administration",
  "users":              "Users & Access",
  "cost-centers":       "Cost Centers",
  "raw-materials":      "Raw Materials",
  "briefs":             "Board Briefs",
  "presentation":       "Presentation Mode",
  "licensing":          "Licensing",
  "inbox":              "Inbox",
  "myday":              "My Day",
  "auth":               "Authentication",
  "callback":           "Callback",
  "notifications":      "Notifications",
  "documents":          "Documents",
  "center":             "",  // dropped in the merged path
  "ai":                 "AI",
  "conversations":      "Ask ORVAN",
  "settings":           "Settings",
  "signals":            "Signals",
  "reports":            "Reports",
  "analytics":          "Analytics",
  "meetings":           "Meetings",
  "pmo":                "PMO",
  "okrs":               "OKRs",
  "risks":              "Risk Register",
  "timeline":           "Timeline",
  "approvals":          "Approvals",
  "team":               "Team",
  "people":             "People",
  "departments":        "Departments",
  "investors":          "Investors",
  "decisions":          "Decisions",
  "scorecards":         "Scorecards",
  "customers":          "Customers",
  "procurement":        "Procurement",
  "fulfillment":        "Fulfillment",
  "suppliers":          "Suppliers",
  "quality":            "Quality",
  "batches":            "Manufacturing",
  "orders":             "Orders",
  "products":           "Catalogue",
  "pricing":            "Pricing & Promos",
  "sync":               "Shopify Sync",
  "checklist":          "Checklist",
  "control":            "Launch Control",
  "preorders":          "Pre-Orders",
  "subscribers":        "Subscribers",
  "sales":              "Sales Pipeline",
  "retention":          "Retention",
  "attribution":        "Attribution",
  "calendar":           "Calendar",
  "creative":           "Creative Review",
  "copy":               "Copy Assistant",
  "creators":           "Creators",
  "channels":           "Channels",
  "formulas":           "Formula Manager",
  "library":            "Formula Bank",
  "lab":                "Lab Sessions",
  "sampling":           "Sampling",
  "blind":              "Blind Testing",
  "budget":             "Budget",
  "taxes":              "Taxes",
  "vendors":            "Vendor Payments",
};

function humanize(seg: string): string {
  if (seg in HUMANIZE) return HUMANIZE[seg];
  // Convert kebab-case / snake_case to Title Case
  return seg.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Given a pathname, return an array of {label, to} crumbs.
 * The first crumb is always Pure H OS (root).
 */
function buildCrumbs(pathname: string): { label: string; to?: string }[] {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  const crumbs: { label: string; to?: string }[] = [{ label: "Pure H OS", to: "/pureh" }];

  // If the exact path matches a registered nav item, resolve the domain.
  const navItem = NAV_ITEM_BY_PATH[pathname];
  if (navItem) {
    const domain = NAV_DOMAINS.find((d) => d.items.some((it) => it.key === navItem.key));
    if (domain) crumbs.push({ label: domain.label });
    crumbs.push({ label: navItem.label, to: navItem.to });
    return crumbs;
  }

  // Otherwise fall back to path segments; skip the leading `pureh`.
  let acc = "";
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    acc += "/" + seg;
    if (i === 0 && seg === "pureh") continue;
    const label = humanize(seg);
    if (!label) continue;
    // Middle crumbs get links; the last one does not.
    crumbs.push({ label, to: i === parts.length - 1 ? undefined : acc });
  }
  return crumbs;
}

export default function Breadcrumbs({ className = "" }: { className?: string }) {
  const location = useLocation();
  const crumbs = buildCrumbs(location.pathname);

  // Do not render on the login/callback pages.
  if (location.pathname === "/pureh/login" || location.pathname.startsWith("/pureh/auth/callback")) {
    return null;
  }
  if (crumbs.length <= 1) return null;

  return (
    <nav className={`flex items-center gap-1 text-xs text-slate-500 ${className}`} data-testid="breadcrumbs" aria-label="Breadcrumb">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight size={11} className="text-slate-400" />}
            {c.to && !last
              ? <Link to={c.to} className="hover:text-slate-800 hover:underline decoration-dotted underline-offset-2">{c.label}</Link>
              : <span className={last ? "text-slate-800 font-medium" : ""} data-testid={last ? "breadcrumbs-current" : undefined}>{c.label}</span>
            }
          </span>
        );
      })}
    </nav>
  );
}
